import { useEffect, useRef, useState } from "react"
import { Button } from "./button"
import { dayNames, monthNames } from "./rangePicker/calendarPanel";
import {cn} from "../lib/fns";
import {CalendarSvg} from "../assets/svgs/CalendarSvg";
import {DoubleLeftOutlined} from "../assets/svgs/DoubleLeftOutlined";
import {LeftOutlined} from "../assets/svgs/LeftOutlined";
import {RightOutlined} from "../assets/svgs/RightOutlined";
import {DoubleRightOutlined} from "../assets/svgs/DoubleRightOutlined";

interface DatePickerProps {
  value?: Date
  onChange?: (date: Date) => void
  placeholder?: string
  className?: string
  size?: "sm" | "md" | "lg"
}

export function DatePicker({
                             value,
                             onChange,
                             placeholder = "Select date",
                             className,
                             size = "md",
                           }: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [currentDate, setCurrentDate] = useState(value || new Date())
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()

    const days: (Date | null)[] = []

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null)
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day))
    }

    return days
  }

  const handlePrevYear = () => {
    setCurrentDate(new Date(currentDate.getFullYear() - 1, currentDate.getMonth(), 1))
  }

  const handleNextYear = () => {
    setCurrentDate(new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), 1))
  }

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))
  }

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))
  }

  const handleToday = () => {
    const today = new Date()
    setCurrentDate(today)
    onChange?.(today)
    setIsOpen(false)
  }

  const handleDateSelect = (date: Date) => {
    onChange?.(date)
    setIsOpen(false)
  }

  const isToday = (date: Date | null) => {
    if (!date) return false
    const today = new Date()
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    )
  }

  const isSelected = (date: Date | null) => {
    if (!date || !value) return false
    return (
      date.getDate() === value.getDate() &&
      date.getMonth() === value.getMonth() &&
      date.getFullYear() === value.getFullYear()
    )
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
  }

  const days = getDaysInMonth(currentDate)
  const buttonSize = size === "md" ? "default" : size

  return (
    <div ref={containerRef} className={cn("relative inline-block", className)}>
      {/* Input Field */}
      <Button
        size={buttonSize}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={cn("flex-1 text-neutral-900 font-normal", !value && "text-muted-foreground")}>
          {value ? formatDate(value) : placeholder}
        </span>
        <CalendarSvg className="h-4 w-4 text-muted-foreground"/>
      </Button>

      {/* Dropdown Calendar */}
      {isOpen && (
        <div
          className="absolute top-full left-0 mt-2 z-50 bg-popover border border-border rounded-lg shadow-lg w-[282px]">
          {/* Header */}
          <div className="h-10 flex items-center justify-between border-b border-neutral-400">
            <div className="flex items-center">
              <Button
                size="icon"
                variant={"text"}
                onClick={handlePrevYear}
              >
                <DoubleLeftOutlined className="h-4 w-4"/>
              </Button>
              <Button
                size="icon"
                variant={"text"}
                onClick={handlePrevMonth}
              >
                <LeftOutlined className="h-4 w-4"/>
              </Button>
            </div>

            <div className="text-sm font-semibold flex-1 text-center">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </div>

            <div className="flex items-center gap-2">
              <Button
                size="icon"
                variant={"text"}
                onClick={handleNextMonth}
              >
                <RightOutlined className="h-4 w-4"/>
              </Button>
              <Button
                size="icon"
                variant={"text"}
                onClick={handleNextYear}
              >
                <DoubleRightOutlined className="h-4 w-4"/>
              </Button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="p-3">
            {/* Day names */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map((day) => (
                <div key={day} className="text-center text-xs font-medium text-muted-foreground py-1">
                  {day}
                </div>
              ))}
            </div>

            {/* Days */}
            <div className="grid grid-cols-7 gap-1">
              {days.map((date, index) => (
                <button
                  key={index}
                  disabled={!date}
                  onClick={() => date && handleDateSelect(date)}
                  className={cn(
                    "h-8 w-8 font-normal rounded-[6px] text-sm",
                    isToday(date) && !isSelected(date) && "border border-primary",
                    isSelected(date) && "bg-accent"
                  )}
                >
                  {date?.getDate()}
                </button>
              ))}
            </div>
          </div>

          {/* Today button */}
          <div className="h-[38px] border-t border-neutral-200 flex justify-center">
            <Button size="sm" variant={"text"} onClick={handleToday} className="text-primary font-medium">
              Today
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
